import type { Store } from './port';

/**
 * Reiht alle Zugriffe auf eine Ablage hintereinander.
 *
 * ⚠️ **Der Wettlauf, den das schliesst** ([[tx-wettlauf-ablage]]): die Adapter kennen nur ein
 * Flag `inTx` für die ganze Ablage. Es konnte „Aufruf aus meiner eigenen Transaktion“ nicht von
 * „fremder Aufrufer zur selben Zeit“ unterscheiden. Zwei Folgen:
 *
 * 1. Ein `tx()` während eines fremden `put()` warf „nicht verschachtelbar“ — beim Autospeichern
 *    neben dem Senden war das `writeFailed` beim Prüfer.
 * 2. Ein `put()` während einer fremden Transaktion schrieb still **in** sie hinein und rollte
 *    mit ihr zurück.
 *
 * Hier deshalb: jeder Zugriff von aussen hängt sich hinten an. Was **innerhalb** einer
 * Transaktion geschieht, geht über den Griff `t`, den `tx(fn)` an `fn` reicht, direkt an den
 * Adapter — nicht durch die Reihe, sonst wartete die Transaktion auf sich selbst.
 *
 * ⛔ **Innerhalb von `fn` nie die äussere Ablage benutzen** (`store`, `getStore()`): der Aufruf
 * stellte sich hinter die laufende Transaktion und wartete für immer.
 */
export function gereiht(roh: Store): Store {
  let schwanz: Promise<unknown> = Promise.resolve();

  const reihe = <T>(schritt: () => Promise<T>): Promise<T> => {
    const lauf = schwanz.then(schritt);
    // Ein Fehler gehört dem Aufrufer, nicht der Reihe: die nächsten laufen trotzdem.
    schwanz = lauf.catch(() => undefined);
    return lauf;
  };

  const griff: Store = {
    open: () => Promise.reject(new Error('open() innerhalb einer Transaktion')),
    put: (table, rows) => roh.put(table, rows),
    all: (table, where) => roh.all(table, where),
    remove: (table, where) => roh.remove(table, where),
    tx: () =>
      Promise.reject(new Error('tx() ist nicht verschachtelbar — eine Transaktion läuft bereits')),
    close: () => Promise.reject(new Error('close() innerhalb einer Transaktion')),
  };

  return {
    open: () => reihe(() => roh.open()),
    put: (table, rows) => reihe(() => roh.put(table, rows)),
    all: (table, where) => reihe(() => roh.all(table, where)),
    remove: (table, where) => reihe(() => roh.remove(table, where)),
    tx: (fn) => reihe(() => roh.tx(() => fn(griff))),
    close: () => reihe(() => roh.close()),
  };
}
